import classNames from 'classnames';
import { useEffect } from 'react';
import {
  useGetZipCodesCSV,
  PendingIcon,
  type ContractorGetCampaignModel,
  type ManagerGetCampaignModel,
} from 'entities/campaign';
import { Button, DownloadCsvButton } from 'shared/ui/Button';
import { useModalsActions } from 'shared/ui/SideModal';
import { Tooltip } from 'shared/ui/Tooltip';
import { getDataAutoTestAttributes } from 'shared/lib/data-auto-test';
import { getApiType } from 'shared/lib/api';
import { useContractorDateWithTimeZone } from 'shared/lib/timezones';

import { ZipsEditorModal } from './ZipsEditorModal';
import { useNextBillingStartAt, useZipsNumber } from './UseZipsStore';

const BLOCKING_EDIT_ZIPS_MESSAGE = 'Target area editing is possible from 8:00 AM EST Monday to 10:00 AM EST Friday';

interface EditZipsProps {
  isEditMode: boolean;
  pendingChanges: boolean;
  isBlockingTime: boolean;
  campaign: ManagerGetCampaignModel | ContractorGetCampaignModel;
}

export function EditZips({ isEditMode, pendingChanges, isBlockingTime, campaign }: EditZipsProps) {
  const zipsNumber = useZipsNumber();
  const numberOfZipCodes = zipsNumber || campaign.numberOfZipCodes;
  const zipsWasChanged = !!useNextBillingStartAt();
  const nextBillingStartAt = useNextBillingStartAt() || campaign.nextBillingStartAt;
  const isDownloadZips = getApiType() === 'internal';
  const { open, closeAllBesidesFirst } = useModalsActions();
  const useGetZipCodesCSVQuery = useGetZipCodesCSV(campaign.id);
  const getPendingDate = useContractorDateWithTimeZone();

  useEffect(() => {
    if (isBlockingTime) {
      closeAllBesidesFirst();
    }
  }, [isBlockingTime]);

  return (
    <div
      data-testid="TestId_TargetArea"
      className="mb-4 flex justify-between"
    >
      <div className="pl-4">
        <div
          className={classNames('text-[11px] font-medium', {
            'text-slate-400': !isEditMode,
            'text-slate-600': isEditMode,
          })}
        >
          Target area
        </div>
        <div
          {...getDataAutoTestAttributes(['div-zip-codes-count']).attributes}
          className={classNames('text-sm font-medium', {
            'text-slate-400': !isEditMode,
          })}
        >
          {numberOfZipCodes.toLocaleString('en-US')} zip codes
        </div>
      </div>
      <div className="flex w-49 items-center justify-end gap-2">
        {(pendingChanges || zipsWasChanged) && (
          <Tooltip
            contentClass="!text-left"
            placement="bottom-end"
            content={`ZIP Code changes will be applied on ${getPendingDate(nextBillingStartAt || '')}`}
          >
            <PendingIcon className="cursor-pointer" />
          </Tooltip>
        )}
        <div className="flex w-43 gap-2">
          {isEditMode && (
            <>
              <DownloadCsvButton
                {...getDataAutoTestAttributes(['button-download-zips'])}
                className={classNames({
                  'opacity-0 pointer-events-none': !isDownloadZips,
                })}
                onClick={useGetZipCodesCSVQuery.download}
                isLoading={useGetZipCodesCSVQuery.isLoading}
                text=""
              />
              <Tooltip
                isDisabled={!isBlockingTime}
                placement="bottom-end"
                content={BLOCKING_EDIT_ZIPS_MESSAGE}
              >
                <Button
                  type="button"
                  disabled={isBlockingTime}
                  className="w-25"
                  onClick={() => {
                    open(<ZipsEditorModal campaignId={campaign.id} />);
                  }}
                  variant="outline"
                  size="medium"
                  {...getDataAutoTestAttributes(['button-edit-zips'])}
                >
                  Edit ZIPs
                </Button>
              </Tooltip>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
